import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import inventoryService from '../services/inventoryService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const InventoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    itemName: '',
    itemCode: '',
    category: 'Spare Parts',
    quantity: '',
    unit: 'pieces',
    minStockLevel: '10',
    price: '',
    unitSalePrice: '',
    supplierName: '',
    supplierContact: '',
    supplierEmail: '',
    rackLocation: '',
    binLocation: '',
    sectionLocation: '',
  });

  const categories = [
    'Spare Parts',
    'Consumables',
    'Refrigerant',
    'Electrical',
    'Filters',
    'Compressor',
    'Motors',
    'Other',
  ];

  const units = ['pieces', 'kg', 'liter', 'meter', 'box', 'packet', 'ft'];

  useEffect(() => {
    if (isEdit) {
      fetchItem();
    }
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await inventoryService.getById(id);
      const item = response.data;
      setFormData({
        itemName: item.itemName,
        itemCode: item.itemCode,
        category: item.category,
        quantity: item.quantity,
        unit: item.unit,
        minStockLevel: item.minStockLevel,
        price: item.price,
        unitSalePrice: item.unitSalePrice ?? '',
        supplierName: item.supplier?.name || '',
        supplierContact: item.supplier?.contact || '',
        supplierEmail: item.supplier?.email || '',
        rackLocation: item.location?.rack || '',
        binLocation: item.location?.bin || '',
        sectionLocation: item.location?.section || '',
      });
    } catch (error) {
      toast.error('Failed to fetch item details');
      navigate('/inventory');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      itemName: formData.itemName,
      itemCode: formData.itemCode,
      category: formData.category,
      quantity: Number(formData.quantity),
      unit: formData.unit,
      minStockLevel: Number(formData.minStockLevel),
      price: Number(formData.price),
      unitSalePrice:
        formData.unitSalePrice === '' ? undefined : Number(formData.unitSalePrice),
      supplier: {
        name: formData.supplierName,
        contact: formData.supplierContact,
        email: formData.supplierEmail,
      },
      location: {
        rack: formData.rackLocation,
        bin: formData.binLocation,
        section: formData.sectionLocation,
      },
    };

    try {
      if (isEdit) {
        await inventoryService.update(id, submitData);
        toast.success('Item updated successfully');
      } else {
        await inventoryService.create(submitData);
        toast.success('Item added successfully');
      }
      navigate('/inventory');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/inventory')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Inventory Item' : 'Add New Item'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Item Information</h3>
          <div className="form-grid">
            <Input
              label="Item Name"
              type="text"
              name="itemName"
              value={formData.itemName}
              onChange={handleChange}
              placeholder="AC Filter - 1.5 Ton"
              required
            />

            <Input
              label="Item Code"
              type="text"
              name="itemCode"
              value={formData.itemCode}
              onChange={handleChange}
              placeholder="INV001"
              required
              disabled={isEdit}
            />

            <div className="input-group">
              <label className="input-label">
                Category <span className="required-asterisk">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="input-field"
                required
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Purchase Price (₹)"
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="2000"
              min="0"
              step="0.01"
              required
            />

            <Input
              label="Unit Sale Price (₹)"
              type="number"
              name="unitSalePrice"
              value={formData.unitSalePrice}
              onChange={handleChange}
              placeholder="1200"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Stock Details</h3>
          <div className="form-grid">
            <Input
              label="Current Quantity"
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              placeholder="50"
              min="0"
              required
            />

            <div className="input-group">
              <label className="input-label">
                Unit <span className="required-asterisk">*</span>
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="input-field"
                required
              >
                {units.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Minimum Stock Level"
              type="number"
              name="minStockLevel"
              value={formData.minStockLevel}
              onChange={handleChange}
              placeholder="10"
              min="0"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Supplier Information</h3>
          <div className="form-grid">
            <Input
              label="Supplier Name"
              type="text"
              name="supplierName"
              value={formData.supplierName}
              onChange={handleChange}
              placeholder="ABC Suppliers"
            />

            <Input
              label="Contact Number"
              type="tel"
              name="supplierContact"
              value={formData.supplierContact}
              onChange={handleChange}
              placeholder="9876543210"
            />

            <Input
              label="Email"
              type="email"
              name="supplierEmail"
              value={formData.supplierEmail}
              onChange={handleChange}
              placeholder="supplier@example.com"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Storage Location</h3>
          <div className="form-grid">
            <Input
              label="Rack"
              type="text"
              name="rackLocation"
              value={formData.rackLocation}
              onChange={handleChange}
              placeholder="A"
            />

            <Input
              label="Bin"
              type="text"
              name="binLocation"
              value={formData.binLocation}
              onChange={handleChange}
              placeholder="1"
            />

            <Input
              label="Section"
              type="text"
              name="sectionLocation"
              value={formData.sectionLocation}
              onChange={handleChange}
              placeholder="Filters"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/inventory')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Item' : 'Add Item'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InventoryForm;
